<template>
  <div id="app">
    <!-- ① 顶部加载进度条（路由跳转 & 初始化） -->
    <div class="top-progress" :class="{ active: isPageLoading }">
      <div class="top-progress-fill"></div>
    </div>

    <!-- ② 全局加载遮罩（首次获取用户信息） -->
    <transition name="fade">
      <div v-if="isGlobalLoading" class="global-overlay" role="status" aria-live="polite">
        <div class="global-spinner">
          <div class="spinner-ring"></div>
          <span>加载中…</span>
        </div>
      </div>
    </transition>

    <!-- ③ 离线横幅 -->
    <transition name="banner-drop">
      <div v-if="!isOnline" class="offline-banner" role="alert" aria-live="assertive">
        <svg class="offline-banner__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
        当前网络已断开，部分功能暂时不可用
      </div>
    </transition>

    <!-- ④ Toast 通知（含重试按钮） -->
    <transition name="toast-drop">
      <div
        v-if="toast.show"
        class="toast"
        :class="toast.type"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <svg class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path v-if="toast.type === 'success'" d="M20 6 9 17l-5-5" />
          <template v-else-if="toast.type === 'error'">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </template>
          <template v-else-if="toast.type === 'warning'">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </template>
          <template v-else>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </template>
        </svg>
        <span class="toast-text">{{ toast.message }}</span>
        <button v-if="toast.retryFn" class="toast-retry" @click="toast.retryFn">重试</button>
        <button class="toast-close" @click="closeToast" aria-label="关闭提示">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>
    </transition>

    <!-- ⑤ 头像上传成功预览浮层 -->
    <transition name="pop">
      <div v-if="uploadSuccessVisible" class="upload-success-card" aria-live="polite">
        <img :src="successAvatarPreview" class="success-avatar" alt="新头像预览" />
        <span class="success-label">
          <span class="check-badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg></span>
          头像更新成功！
        </span>
      </div>
    </transition>

    <!-- ⑥ 新用户引导 -->
    <transition name="pop">
      <div
        v-if="guideVisible"
        class="user-guide-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-title"
      >
        <div class="guide-box">
          <div class="guide-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5" /><path d="M20 21a8 8 0 0 0-16 0" /></svg>
          </div>
          <h4 id="guide-title">欢迎使用！</h4>
          <p>点击右上角头像可上传个人头像；悬停头像可查看账户余额并充值。</p>
          <button class="guide-ok" ref="guideOkBtn" @click="closeGuide">知道了</button>
        </div>
      </div>
    </transition>

    <!-- 主导航 -->
    <nav class="navigation">
      <div class="nav-wrapper">
        <div class="left-nav">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /></svg>
          </span>
          <router-link to="/" class="hover-effect">首页</router-link>
        </div>

        <!-- 桌面端主导航 -->
        <div class="main-navigation" role="menubar" aria-label="主功能导航">
          <router-link to="/video-download" class="hover-effect" role="menuitem">视频下载</router-link>
          <router-link to="/audio-processing" class="hover-effect" role="menuitem">音频处理</router-link>
          <router-link to="/video-translation" class="hover-effect" role="menuitem">视频原声翻译</router-link>
          <router-link to="/stepwise-video-translation" class="hover-effect" role="menuitem">精细视频翻译</router-link>
          <router-link to="/recode" class="hover-effect" role="menuitem">用户操作记录</router-link>
        </div>

        <div class="right-nav">
          <!-- 已登录：头像 + 下拉 -->
          <div
            v-if="isLoggedIn"
            class="avatar-dropdown"
            @mouseover="handleMouseOver"
            @mouseleave="handleMouseLeave"
          >
            <div
              class="avatar-container"
              tabindex="0"
              role="button"
              aria-label="上传头像"
              :aria-expanded="showDropdown"
              aria-haspopup="menu"
              @click="handleAvatarImageClick"
              @keydown.enter.prevent="handleAvatarImageClick"
              @keydown.space.prevent="handleAvatarImageClick"
            >
              <img :src="avatarUrl" alt="用户头像" />
              <!-- 上传进度遮罩 -->
              <div v-if="isUploading" class="upload-mask" aria-hidden="true">
                <svg class="spin-svg" viewBox="0 0 44 44" width="26" height="26">
                  <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.9)"
                    stroke-width="4" stroke-linecap="round" stroke-dasharray="60 30" />
                </svg>
                <span v-if="uploadProgress > 0" class="upload-pct">{{ uploadProgress }}%</span>
              </div>
              <input
                type="file"
                ref="fileInput"
                accept=".jpg,.jpeg,.png"
                style="display:none"
                aria-hidden="true"
                @change="handleFileChange"
              />
            </div>
          </div>
          <!-- 未登录 -->
          <router-link v-else to="/loginRegister" class="hover-effect login-link">登录/注册</router-link>

          <!-- 汉堡菜单按钮（移动端） -->
          <button
            class="hamburger"
            :class="{ open: drawerOpen }"
            @click="toggleDrawer"
            :aria-expanded="drawerOpen"
            aria-controls="mobile-nav-drawer"
            aria-label="打开导航菜单"
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>

      <!-- 桌面端下拉菜单 -->
      <transition name="dropdown-slide">
        <div
          v-if="isLoggedIn && showDropdown"
          class="dropdown-content"
          role="menu"
          aria-label="账户操作菜单"
          @mouseover="clearHideTimeout"
          @mouseleave="startHideDropdown"
        >
          <div
            class="balance-row"
            @mouseenter="balanceTipVisible = true"
            @mouseleave="balanceTipVisible = false"
          >
            <span class="balance-text">账户余额：{{ formattedBalance }}</span>
            <transition name="fade">
              <div v-if="balanceTipVisible" class="balance-tooltip" role="tooltip">
                <div class="tooltip-item">可用余额：{{ formattedBalance }}</div>
                <div class="tooltip-sub">更多记录请前往账户页面</div>
              </div>
            </transition>
            <button
              class="recharge-button"
              role="menuitem"
              aria-label="充值账户"
              @click="handlePayButtonClick"
            >充值</button>
          </div>
          <button
            class="logout-button"
            role="menuitem"
            aria-label="退出登录"
            @click="clickLogout"
          >退出登录</button>
        </div>
      </transition>
    </nav>

    <!-- 移动端侧边抽屉 -->
    <transition name="drawer-slide">
      <nav
        v-if="drawerOpen"
        id="mobile-nav-drawer"
        class="mobile-drawer"
        role="navigation"
        aria-label="移动端导航菜单"
      >
        <div class="drawer-head">
          <span>菜单</span>
          <button class="drawer-close-btn" @click="closeDrawer" aria-label="关闭菜单">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>
        <div class="drawer-links">
          <router-link to="/" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
            首页
          </router-link>
          <router-link to="/video-download" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m22 8-6 4 6 4V8Z" /><rect width="14" height="12" x="2" y="6" rx="2" ry="2" /></svg>
            视频下载
          </router-link>
          <router-link to="/audio-processing" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
            音频处理
          </router-link>
          <router-link to="/video-translation" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></svg>
            视频原声翻译
          </router-link>
          <router-link to="/stepwise-video-translation" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            精细视频翻译
          </router-link>
          <router-link to="/recode" class="drawer-link" @click.native="closeDrawer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" /></svg>
            用户操作记录
          </router-link>
        </div>
        <div class="drawer-foot">
          <template v-if="isLoggedIn">
            <p class="drawer-balance">余额：{{ formattedBalance }}</p>
            <button class="drawer-action recharge-action" @click="handlePayButtonClick(); closeDrawer()">充值</button>
            <button class="drawer-action logout-action" @click="clickLogout(); closeDrawer()">退出登录</button>
          </template>
          <router-link v-else to="/loginRegister" class="drawer-action login-action" @click.native="closeDrawer">登录/注册</router-link>
        </div>
      </nav>
    </transition>
    <!-- 抽屉遮罩 -->
    <transition name="fade">
      <div v-if="drawerOpen" class="drawer-overlay" @click="closeDrawer" aria-hidden="true"></div>
    </transition>

    <main class="content">
      <router-view />
    </main>
    <!-- 在 <template> 底部添加（视觉上隐藏，但搜索引擎可读） -->
    <seo-content style="position:absolute;left:-9999px;top:-9999px;" aria-hidden="true">
      <h1>灵动帧AI多媒体处理平台 - 深圳灵动帧智能科技有限公司</h1>
      <p>提供YouTube视频下载、Bilibili视频下载、视频字幕自动生成、说话人标签识别、音频翻译、视频翻译、字幕翻译、视频对口型、语音克隆等AI服务。</p>
      <p>支持功能：智能字幕生成（带说话人标识）、多语种视频翻译（中英日韩德法等）、音频降噪、语音合成、视频超分辨率。</p>
      <p>深圳灵动帧智能科技有限公司致力于为创作者、教育机构、企业提供高效智能的多媒体处理SaaS工具。</p>
    </seo-content>
  </div>
</template>

<script>
import { getUpQiniuToken, saveAvatarUrl, uploadToQiniu, getUserInfo } from '@/api/upfile'
import { defAvatarUrl, QINIU_BASE_URL } from '@/api/constants'
import { mapGetters, mapActions } from 'vuex'

// 百度主动推送工具函数
function pushBaiduLink (path) {
  if (process.env.NODE_ENV === 'production') {
    const urls = [`https://www.lingdongzhen.com${path}`]
    const BAIDU_PUSH_TOKEN = 'yr6eVoziH0McOFC8'
    fetch(`http://data.zz.baidu.com/urls?site=https://www.lingdongzhen.com&token=${BAIDU_PUSH_TOKEN}`, {
      method: 'POST',
      body: urls.join('\n'),
      headers: { 'Content-Type': 'text/plain' }
    }).catch(err => console.error('百度推送失败:', err))
  }
}

const CACHE_KEY = 'avatar_url_cache'
const CACHE_TS_KEY = 'avatar_url_cache_ts'
const CACHE_TTL = 30 * 60 * 1000 // 30分钟

function safeStorage (fn) {
  try { return fn() } catch { return null }
}

export default {
  name: 'homeVue',
  data () {
    return {
      avatarUrl: defAvatarUrl,
      showDropdown: false,
      hideTimeout: null,
      isUploading: false,
      uploadProgress: 0,
      isPageLoading: false,
      isGlobalLoading: false,
      isOnline: navigator.onLine,
      drawerOpen: false,
      uploadSuccessVisible: false,
      successAvatarPreview: '',
      guideVisible: false,
      balanceTipVisible: false,
      prevFocus: null,
      retryCount: 0,
      retryTimer: null,
      toastTimer: null,
      // 网络错误提示控制字段
      hasShownInitError: false, // 是否已显示初始化错误提示（防止重复弹窗）
      isRetrying: false, // 是否处于静默重试状态
      toast: {
        show: false,
        message: '',
        type: 'info',
        retryFn: null
      }
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'balance', 'formattedBalance'])
  },
  created () {
    this.initUserAuth()
    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
    document.removeEventListener('keydown', this.onKeydown)
    clearTimeout(this.hideTimeout)
    clearTimeout(this.toastTimer)
    clearTimeout(this.retryTimer)
  },
  watch: {
    isLoggedIn (val) {
      if (val) {
        this.fetchUserAvatar()
        // 新用户引导
        const seen = safeStorage(() => localStorage.getItem('guide_seen'))
        if (!seen) {
          setTimeout(() => {
            this.guideVisible = true
            this.$nextTick(() => this.$refs.guideOkBtn && this.$refs.guideOkBtn.focus())
          }, 800)
        }
      } else {
        this.resetUserState()
      }
    },
    '$route' (to) {
      // 路由切换时显示顶部进度条
      this.isPageLoading = true
      this.closeDrawer()
      setTimeout(() => { this.isPageLoading = false }, 400)
      // 路由变化时推送当前页面到百度
      pushBaiduLink(to.path)
    }
  },
  methods: {
    ...mapActions(['logout']),

    // ---- 网络监听 ----
    onOnline () {
      this.isOnline = true
      // 网络恢复后，如果之前因网络错误失败过，自动静默重试
      if (this.hasShownInitError && this.isLoggedIn) {
        this.fetchUserAvatar()
      }
      this.showToast('网络已恢复连接', 'success')
    },
    onOffline () {
      this.isOnline = false
    },

    // ---- 键盘 Escape 关闭所有浮层 ----
    onKeydown (e) {
      if (e.key === 'Escape') {
        if (this.guideVisible) this.closeGuide()
        else if (this.drawerOpen) this.closeDrawer()
        else if (this.showDropdown) this.showDropdown = false
      }
    },

    // ---- 初始化 ----
    async initUserAuth () {
      if (this.isLoggedIn) {
        this.isGlobalLoading = true
        try {
          await this.fetchUserAvatar()
        } finally {
          this.isGlobalLoading = false
        }
      } else {
        this.resetUserState()
      }
    },

    // ---- 获取用户信息（优化重试提示逻辑） ----
    async fetchUserAvatar (isRetry = false) {
      if (!isRetry) {
        this.retryCount = 0
        this.isRetrying = false
      }

      try {
        const res = await getUserInfo()
        if (res && res.status === 200 && res.data) {
          const url = res.data.avatar_url || defAvatarUrl
          this.avatarUrl = url
          safeStorage(() => {
            localStorage.setItem(CACHE_KEY, url)
            localStorage.setItem(CACHE_TS_KEY, String(Date.now()))
          })
          if (res.data.account_balance != null) {
            const b = parseFloat(res.data.account_balance)
            this.$store.commit('setBalance', isNaN(b) ? 0 : b)
          }
          // 请求成功：重置所有错误状态
          this.hasShownInitError = false
          this.retryCount = 0
          this.isRetrying = false
        } else {
          this.handleAuthFailure('获取用户信息失败')
        }
      } catch (err) {
        console.error('fetchUserAvatar error:', err)

        // 认证类错误：立即处理，不重试
        if (err.response?.status === 401 || err.message?.includes('token')) {
          const msg = err.response?.status === 401
            ? '登录已过期，请重新登录'
            : '身份验证失败，请重新登录'
          this.handleAuthFailure(msg)
          return
        }

        // 网络错误处理（断网/超时/服务器无响应）
        const isNetworkError =
          err.response?.status === 0 ||
          err.message?.includes('Network Error') ||
          err.message?.includes('timeout') ||
          !err.response

        if (isNetworkError) {
          // 使用缓存头像保证基础体验
          this.useCachedAvatar()

          // 核心优化：只在首次失败时提示一次
          if (!this.hasShownInitError && !this.isRetrying) {
            this.isRetrying = true
            this.hasShownInitError = true
            // 显示统一提示，5 秒自动消失，支持手动关闭
            this.showToast('后端服务器异常，请稍后重试', 'error')
          }

          // 静默指数退避重试（不显示 toast，避免打扰）
          const MAX_RETRY = 3
          if (this.retryCount < MAX_RETRY) {
            const delay = 1000 * Math.pow(2, this.retryCount) // 1s, 2s, 4s
            this.retryCount++
            this.retryTimer = setTimeout(() => {
              this.fetchUserAvatar(true) // 静默重试
            }, delay)
          }
          // 达到最大重试次数后不再提示，避免重复打扰用户
          return
        }

        // 其他业务错误：正常处理
        this.handleAuthFailure('获取用户信息失败')
      }
    },

    useCachedAvatar () {
      safeStorage(() => {
        const url = localStorage.getItem(CACHE_KEY)
        const ts = Number(localStorage.getItem(CACHE_TS_KEY))
        if (url && Date.now() - ts < CACHE_TTL) this.avatarUrl = url
      })
    },

    handleAuthFailure (msg) {
      this.showToast(msg, 'warning')
      this.performLogout(false)
    },

    resetUserState () {
      this.avatarUrl = defAvatarUrl
      this.showDropdown = false
      this.$store.commit('setBalance', 0)
    },

    // ---- Toast ----
    showToast (message, type = 'info', retryFn = null) {
      clearTimeout(this.toastTimer)
      this.toast = { show: true, message, type, retryFn }
      // 5000ms = 5 秒
      this.toastTimer = setTimeout(() => { this.toast.show = false }, 5000)
    },
    closeToast () {
      clearTimeout(this.toastTimer)
      this.toast.show = false
    },

    // ---- 头像上传 ----
    handleAvatarImageClick () {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'warning'); return }
      if (this.isUploading) { this.showToast('头像上传中，请稍候', 'warning'); return }
      if (!this.isOnline) { this.showToast('当前无网络连接', 'warning'); return }
      this.$refs.fileInput.click()
    },

    async handleFileChange (event) {
      const file = event.target.files[0]
      if (!file) return

      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        this.showToast('仅支持 JPG / PNG 格式的图片', 'error')
        return
      }
      if (file.size > 2 * 1024 * 1024) {
        this.showToast('图片大小不能超过 2MB', 'error')
        return
      }

      this.isUploading = true
      this.uploadProgress = 0

      try {
        const ext = file.name.split('.').pop()
        const name = `avatar_${Date.now()}.${ext}`

        this.uploadProgress = 20
        const tokenRes = await getUpQiniuToken()
        if (!tokenRes?.uploadToken) throw new Error('获取上传凭证失败')

        this.uploadProgress = 45
        const upRes = await uploadToQiniu(file, name, tokenRes.uploadToken)
        this.uploadProgress = 80
        if (!upRes?.data?.key) throw new Error('上传失败')

        const newUrl = `${QINIU_BASE_URL}/${upRes.data.key}`
        await saveAvatarUrl({ file_qiniu_url: newUrl, file_name: upRes.data.key })
        this.uploadProgress = 100

        this.avatarUrl = newUrl
        safeStorage(() => {
          localStorage.setItem(CACHE_KEY, newUrl)
          localStorage.setItem(CACHE_TS_KEY, String(Date.now()))
        })

        // 显示成功预览浮层
        this.successAvatarPreview = newUrl
        this.uploadSuccessVisible = true
        setTimeout(() => { this.uploadSuccessVisible = false }, 3000)

        this.showToast('头像上传成功', 'success')
      } catch (err) {
        console.error('头像上传失败:', err)
        if (err.response?.status === 401) {
          this.handleAuthFailure('登录已过期，请重新登录')
        } else {
          this.showToast(
            `上传失败：${err.message || '请重试'}`,
            'error',
            () => this.handleAvatarImageClick()
          )
        }
      } finally {
        this.isUploading = false
        this.uploadProgress = 0
        try { this.$refs.fileInput.value = '' } catch {}
      }
    },

    // ---- 登出 ----
    clickLogout () { this.performLogout(true) },
    performLogout (showMsg = true) {
      this.logout()
      this.resetUserState()
      if (this.$route.path !== '/') this.$router.push('/')
      if (showMsg) this.showToast('已退出登录', 'success')
    },

    // ---- 下拉菜单（桌面端） ----
    handleMouseOver () {
      if (this.isLoggedIn) { clearTimeout(this.hideTimeout); this.showDropdown = true }
    },
    handleMouseLeave () {
      this.hideTimeout = setTimeout(() => { this.showDropdown = false }, 400)
    },
    clearHideTimeout () { clearTimeout(this.hideTimeout) },
    startHideDropdown () {
      this.hideTimeout = setTimeout(() => { this.showDropdown = false }, 300)
    },

    // ---- 充值按钮跳转到支付页面 ----
    handlePayButtonClick () {
      if (!this.isLoggedIn) { this.showToast('请先登录', 'warning'); return }
      if (!this.isOnline) { this.showToast('当前无网络连接', 'warning'); return }
      if (this.$route.path !== '/pay') {
        this.$router.push('/pay')
      }
    },

    // ---- 移动端抽屉 ----
    toggleDrawer () { this.drawerOpen = !this.drawerOpen },
    closeDrawer () { this.drawerOpen = false },

    // ---- 新用户引导 ----
    closeGuide () {
      this.guideVisible = false
      safeStorage(() => localStorage.setItem('guide_seen', '1'))
    }
  }
}
</script>

<style lang="less" scoped>
// ==================== 设计令牌 ====================
@primary: #3555f4;
@primary-hover: #2a44d0;
@primary-soft: #eef1ff;
@success: #16a34a;
@warning: #d97706;
@danger: #dc2626;
@info: #409eff;

// ============================================================
//  基础
// ============================================================
#app {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Segoe UI", sans-serif;
  font-size: 14px;
}

// ============================================================
//  顶部加载进度条
// ============================================================
.top-progress {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  z-index: 99999;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;

  &.active {
    opacity: 1;
    .top-progress-fill { animation: progress-run 0.45s ease-out forwards; }
  }

  .top-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, rgba(255,255,255,0.5) 0%, #fff 60%, rgba(255,255,255,0.8) 100%);
    width: 0;
    border-radius: 0 2px 2px 0;
    box-shadow: 0 0 8px rgba(255,255,255,0.7);
  }
}

@keyframes progress-run {
  0%   { width: 0; }
  60%  { width: 75%; }
  100% { width: 100%; }
}

// ============================================================
//  全局加载遮罩
// ============================================================
.global-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;

  .global-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    color: #fff;
    font-size: 14px;

    .spinner-ring {
      width: 46px;
      height: 46px;
      border: 4px solid rgba(255, 255, 255, 0.25);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.75s linear infinite;
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// ============================================================
//  离线横幅
// ============================================================
.offline-banner {
  position: fixed;
  top: 60px;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: @warning;
  color: #fff;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  z-index: 9990;
  letter-spacing: 0.3px;

  &__icon {
    width: 16px;
    height: 16px;
  }
}

// ============================================================
//  Toast
// ============================================================
.toast {
  position: fixed;
  top: 74px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px 11px 16px;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.22);
  min-width: 220px;
  max-width: 90vw;

  &.info    { background: @info; }
  &.success { background: @success; }
  &.warning { background: @warning; }
  &.error   { background: @danger; }

  .toast-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .toast-text { flex: 1; }

  .toast-retry,
  .toast-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.22);
    border: none;
    color: #fff;
    cursor: pointer;
    border-radius: 6px;
    font-size: 12px;
    transition: background 0.2s;
    white-space: nowrap;
    &:hover  { background: rgba(255, 255, 255, 0.38); }
    &:focus-visible  { outline: 2px solid rgba(255, 255, 255, 0.8); outline-offset: 1px; }
  }
  .toast-retry { padding: 5px 10px; }
  .toast-close {
    width: 26px;
    height: 26px;
    padding: 0;
    svg { width: 14px; height: 14px; }
  }
}

// ============================================================
//  头像上传成功预览
// ============================================================
.upload-success-card {
  position: fixed;
  top: 78px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px 18px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.16);
  z-index: 9995;

  .success-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    border: 2.5px solid @success;
  }

  .success-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    .check-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: @success;
      color: #fff;
      svg { width: 11px; height: 11px; }
    }
  }
}

// ============================================================
//  新用户引导
// ============================================================
.user-guide-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.46);
  z-index: 9994;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  .guide-box {
    background: #fff;
    border-radius: 18px;
    padding: 34px 28px 28px;
    max-width: 340px;
    width: 100%;
    text-align: center;
    box-shadow: 0 10px 36px rgba(15, 23, 42, 0.18);

    .guide-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: @primary-soft;
      color: @primary;
      margin-bottom: 16px;
      svg { width: 32px; height: 32px; }
    }
    h4 { margin: 0 0 10px; font-size: 18px; color: #1f2937; }
    p  { margin: 0 0 22px; font-size: 14px; color: #64748b; line-height: 1.7; }
    .guide-ok {
      background: @primary;
      color: #fff;
      border: none;
      border-radius: 9px;
      padding: 11px 32px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
      &:hover { background: @primary-hover; }
      &:focus-visible { outline: 2px solid #aab8f9; outline-offset: 2px; }
    }
  }
}

// ============================================================
//  主导航
// ============================================================
nav.navigation {
  position: fixed !important;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 60px;
  padding: 0 60px;
  background: @primary !important;
  z-index: 1000;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);

  .nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    max-width: 1400px;
    margin: 0 auto;
  }

  .left-nav,
  .main-navigation,
  .right-nav {
    height: 100%;
    display: flex;
    align-items: center;
  }

  .left-nav {
    gap: 4px;

    .brand-mark {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 9px;
      background: rgba(255, 255, 255, 0.16);
      color: #fff;
      margin-right: 6px;
      svg { width: 18px; height: 18px; }
    }
  }

  .main-navigation {
    flex-grow: 1;
    justify-content: center;
  }

  .right-nav {
    gap: 6px;

    .avatar-dropdown {
      position: relative;
      display: inline-block;
      cursor: pointer;

      .avatar-container {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        overflow: hidden;
        position: relative;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
        cursor: pointer;
        outline: none;

        &:focus-visible { box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.85); }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
          display: block;
          pointer-events: none;
        }

        // 悬停暗色遮罩
        &::after {
          content: "";
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          opacity: 0;
          transition: opacity 0.3s;
          pointer-events: none;
        }
        &:hover::after { opacity: 1; }
        &:hover img   { transform: scale(1.06); }

        // 上传进度遮罩
        .upload-mask {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.58);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;

          .spin-svg { animation: spin 0.8s linear infinite; }
          .upload-pct { color: #fff; font-size: 10px; font-weight: bold; line-height: 1; }
        }
      }
    }

    .login-link { white-space: nowrap; }

    // 汉堡菜单按钮（默认隐藏，900px 以下显示）
    .hamburger {
      display: none;
      flex-direction: column;
      gap: 5px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px 8px;
      border-radius: 7px;
      margin-left: 6px;
      min-width: 44px;
      min-height: 44px;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;

      &:hover { background: rgba(255, 255, 255, 0.15); }
      &:focus-visible  { outline: 2px solid rgba(255, 255, 255, 0.7); }

      span {
        display: block;
        width: 22px;
        height: 2px;
        background: #fff;
        border-radius: 2px;
        transition: transform 0.28s ease, opacity 0.2s ease;
        transform-origin: center;
      }

      &.open {
        span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        span:nth-child(2) { opacity: 0; }
        span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
      }
    }
  }

  // 导航链接通用样式
  a {
    position: relative;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
    margin: 0 20px;
    padding: 20px 0;
    height: 100%;
    display: inline-flex;
    align-items: center;

    &.router-link-exact-active { color: #ffd166; }

    &:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.8); outline-offset: 2px; }
  }

  .hover-effect {
    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 11px;
      width: 100%;
      height: 2px;
      background: #fff;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    }
    &:hover::after { transform: scaleX(1); }
    &.router-link-exact-active::after {
      transform: scaleX(1);
      background: #ffd166;
    }
  }

  // 桌面端下拉菜单
  .dropdown-content {
    position: absolute;
    top: 68px;
    right: 60px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.14);
    padding: 14px;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 250px;

    .balance-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
      gap: 8px;

      .balance-text {
        font-weight: 600;
        color: #1f2937;
        font-size: 13px;
        flex: 1;
        font-variant-numeric: tabular-nums;
      }

      .balance-tooltip {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        background: #1f2937;
        color: #fff;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 12px;
        min-width: 190px;
        box-shadow: 0 4px 14px rgba(15, 23, 42, 0.22);
        z-index: 10;
        line-height: 1.7;
        &::before {
          content: "";
          position: absolute;
          top: -6px;
          left: 14px;
          border: 6px solid transparent;
          border-top: none;
          border-bottom-color: #1f2937;
        }
        .tooltip-sub { color: rgba(255, 255, 255, 0.6); font-size: 11px; margin-top: 3px; }
      }

      .recharge-button {
        padding: 5px 13px;
        background: @success;
        color: #fff;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        white-space: nowrap;
        min-height: 32px;
        transition: background 0.22s;
        &:hover { background: #15803d; }
        &:focus-visible { outline: 2px solid #15803d; outline-offset: 1px; }
      }
    }
  }
}

// 退出按钮（在 dropdown-content 内，需在外层定义以配合 scoped）
.logout-button {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #1f2937;
  padding: 8px 12px;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  width: 100%;
  transition: all 0.2s;
  &:hover  { background: #e8e8e8; border-color: #bbb; }
  &:active { transform: translateY(1px); }
  &:focus-visible  { outline: 2px solid @primary; outline-offset: 1px; }
}

// ============================================================
//  移动端侧边抽屉
// ============================================================
.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  max-width: 85vw;
  height: 100%;
  background: #fff;
  z-index: 9993;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 28px rgba(15, 23, 42, 0.18);
  overflow-y: auto;

  .drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 0 20px;
    background: @primary;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    height: 60px;
    flex-shrink: 0;

    .drawer-close-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      color: #fff;
      cursor: pointer;
      min-width: 44px;
      min-height: 44px;
      border-radius: 6px;
      transition: background 0.2s;
      svg { width: 20px; height: 20px; }
      &:hover { background: rgba(255, 255, 255, 0.18); }
      &:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.7); }
    }
  }

  .drawer-links {
    flex: 1;
    padding: 8px 0;

    .drawer-link {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 0 24px;
      font-size: 15px;
      color: #1f2937;
      text-decoration: none;
      border-bottom: 1px solid #f5f5f5;
      min-height: 52px;
      transition: background 0.18s;
      svg { width: 20px; height: 20px; color: #64748b; }
      &:hover { background: #f0f4ff; }
      &.router-link-exact-active {
        color: @primary;
        background: @primary-soft;
        font-weight: 600;
        svg { color: @primary; }
      }
      &:focus-visible { outline: 2px solid @primary; outline-offset: -2px; }
    }
  }

  .drawer-foot {
    padding: 16px 20px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .drawer-balance {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      padding: 6px 0;
      font-variant-numeric: tabular-nums;
    }

    .drawer-action {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      text-decoration: none;
      padding: 13px;
      border-radius: 9px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      min-height: 48px;
      transition: all 0.2s;
      &:focus-visible { outline: 2px solid @primary; outline-offset: 1px; }

      &.recharge-action { background: @success; color: #fff; &:hover { background: #15803d; } }
      &.logout-action   { background: #f5f5f5; color: #1f2937; border: 1px solid #ddd; &:hover { background: #e8e8e8; } }
      &.login-action    { background: @primary; color: #fff; &:hover { background: @primary-hover; } }
    }
  }
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.46);
  z-index: 9992;
}

// ============================================================
//  主内容区
// ============================================================
.content {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  width: 95%;
  max-width: 1400px;
  min-height: calc(100vh - 60px);
  margin: 80px auto 0;
}

// ============================================================
//  Vue 过渡动画
// ============================================================

// 通用淡入淡出
.fade-enter-active, .fade-leave-active { transition: opacity 0.22s; }
.fade-enter, .fade-leave-to { opacity: 0; }

// Toast 从顶部滑落（含回弹）
.toast-drop-enter-active {
  animation: toast-in 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.toast-drop-leave-active {
  animation: toast-out 0.22s ease-in forwards;
}
@keyframes toast-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-22px) scale(0.94); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1); }
}
@keyframes toast-out {
  from { opacity: 1; transform: translateX(-50%) translateY(0); }
  to   { opacity: 0; transform: translateX(-50%) translateY(-14px); }
}

// 下拉菜单滑入 + 淡出
.dropdown-slide-enter-active {
  animation: dropdown-in 0.22s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
.dropdown-slide-leave-active {
  animation: dropdown-out 0.18s ease-in forwards;
}
@keyframes dropdown-in {
  from { opacity: 0; transform: translateY(-10px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)     scale(1); }
}
@keyframes dropdown-out {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-8px); }
}

// 弹性缩放弹出（成功预览 / 引导弹窗）
.pop-enter-active { animation: pop-in  0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
.pop-leave-active { animation: pop-out 0.2s ease-in forwards; }
@keyframes pop-in  { from { opacity: 0; transform: scale(0.82); } to { opacity: 1; transform: scale(1); } }
@keyframes pop-out { from { opacity: 1; transform: scale(1);    } to { opacity: 0; transform: scale(0.88); } }

// 离线横幅从顶部滑入
.banner-drop-enter-active { animation: banner-in  0.3s ease forwards; }
.banner-drop-leave-active { animation: banner-out 0.25s ease forwards; }
@keyframes banner-in  { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes banner-out { from { transform: translateY(0);     opacity: 1; } to { transform: translateY(-100%); opacity: 0; } }

// 侧边抽屉从右滑入
.drawer-slide-enter-active { animation: drawer-in  0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
.drawer-slide-leave-active { animation: drawer-out 0.26s ease-in forwards; }
@keyframes drawer-in  { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes drawer-out { from { transform: translateX(0);    } to { transform: translateX(100%); } }

// ============================================================
//  响应式
// ============================================================
@media (max-width: 1200px) {
  nav.navigation {
    padding: 0 30px;
    .dropdown-content { right: 30px; }
    a { margin: 0 14px; }
  }
}

@media (max-width: 900px) {
  nav.navigation {
    padding: 0 20px;
    .main-navigation { display: none; }
    .right-nav .hamburger { display: flex; }
    .dropdown-content { right: 20px; }
  }
}

@media (max-width: 600px) {
  nav.navigation {
    padding: 0 14px;
    .dropdown-content { right: 14px; width: 200px; }
    .left-nav .brand-mark { margin-right: 0; }
  }
  .content {
    width: 100%;
    padding: 0 12px;
    margin-top: 76px;
  }
  .upload-success-card {
    right: 12px;
    top: 70px;
    padding: 10px 14px;
    .success-avatar { width: 42px; height: 42px; }
  }
}

// ============================================================
//  减少动态效果
// ============================================================
@media (prefers-reduced-motion: reduce) {
  .top-progress-fill,
  .spinner-ring,
  .spin-svg,
  .toast-drop-enter-active,
  .dropdown-slide-enter-active,
  .pop-enter-active,
  .banner-drop-enter-active,
  .drawer-slide-enter-active,
  .hover-effect::after,
  .avatar-container img,
  .avatar-container::after {
    animation: none !important;
    transition: none !important;
  }
}
</style>
